import MoveUpIcon from "@mui/icons-material/MoveUp";
import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { range } from "lodash";
import { ReactNode, useState } from "react";

import {
  TierIiReportEncampStatus,
  TierIiReportKind,
} from "../../../../../generated-graphql/graphql";

export const NO_REPORT = "NO_REPORT";
type ReportKindChoice = TierIiReportKind | typeof NO_REPORT;

const REPORT_KIND_OPTIONS: { value: ReportKindChoice; label: string }[] = [
  { value: TierIiReportKind.Annual, label: "Annual" },
  { value: TierIiReportKind.Revision, label: "Revision" },
  { value: TierIiReportKind.Update, label: "Update" },
  { value: TierIiReportKind.Initial, label: "Initial" },
  { value: NO_REPORT, label: "No report" },
];

const isReportKindChoice = (value: string): value is ReportKindChoice =>
  REPORT_KIND_OPTIONS.some((option) => option.value === value);

export type PromoteExtractionChoice = {
  reportingYear: number;
  reportKind: TierIiReportKind | null;
};

type ExistingReport = {
  id: string;
  reportingYear: number;
  reportKind: TierIiReportKind;
  encampStatus: TierIiReportEncampStatus;
};

type Props = {
  open: boolean;
  /** The facility's current reports, used to warn before a promotion overwrites one. */
  existingReports: ExistingReport[];
  loading: boolean;
  onClose: () => void;
  onConfirm: (choice: PromoteExtractionChoice) => void;
  children?: ReactNode;
};

/** Staff choose the reporting year and report kind an extraction becomes; nothing is defaulted. */
export function PromoteExtractionDialog({
  open,
  existingReports,
  loading,
  onClose,
  onConfirm,
  children,
}: Props) {
  const [reportingYear, setReportingYear] = useState<number | "">("");
  const [reportKind, setReportKind] = useState<ReportKindChoice | "">("");
  const currentYear = new Date().getFullYear();
  const reportToOverwrite = existingReports.find(
    (report) => report.reportingYear === reportingYear && report.reportKind === reportKind
  );

  const handleYearChange = (event: SelectChangeEvent<number | "">) => {
    const { value } = event.target;
    setReportingYear(typeof value === "number" ? value : "");
  };

  const handleKindChange = (event: SelectChangeEvent<ReportKindChoice | "">) => {
    const { value } = event.target;
    setReportKind(isReportKindChoice(value) ? value : "");
  };

  const handleConfirm = () => {
    if (reportingYear === "" || reportKind === "") return;
    onConfirm({ reportingYear, reportKind: reportKind === NO_REPORT ? null : reportKind });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Promote extraction"
      subheader="Choose what this extraction becomes in Encamp."
      primaryActionButtonProps={{
        loading,
        disabled: reportingYear === "" || reportKind === "",
        startIcon: <MoveUpIcon />,
        onClick: handleConfirm,
        saveText: "Promote",
      }}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="promote-reporting-year-label">Reporting year</InputLabel>
          <Select<number | "">
            labelId="promote-reporting-year-label"
            label="Reporting year"
            value={reportingYear}
            onChange={handleYearChange}
          >
            {range(currentYear, currentYear - 5).map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel id="promote-report-kind-label">Report kind</InputLabel>
          <Select<ReportKindChoice | "">
            labelId="promote-report-kind-label"
            label="Report kind"
            value={reportKind}
            onChange={handleKindChange}
          >
            {REPORT_KIND_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {reportKind === NO_REPORT && (
          <Alert severity="warning">
            No Tier II report will be created. Facility details and chemical inventory are still
            written to the selected reporting year.
          </Alert>
        )}
        {reportToOverwrite && (
          <Alert severity="warning">
            This facility already has an RY{reportToOverwrite.reportingYear}{" "}
            {prettyPrintEnumValue(reportToOverwrite.reportKind)} report (
            {prettyPrintEnumValue(reportToOverwrite.encampStatus)}). Promoting will overwrite its
            status and verification details with the extraction&apos;s.
          </Alert>
        )}
        {children}
      </Stack>
    </Dialog>
  );
}
